import { useMutation } from '@tanstack/react-query';
import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/Button';
import { Divider } from 'src/components/Divider';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { ClientSearchResult } from 'src/models/user/_types';
import { fetchUser } from 'src/models/user/user';
import { ClientAutocomplete } from 'src/shared/ClientAutocomplete';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';

import { PaymentStep, usePaymentDialogContext } from './PaymentDialogContext';

export const UserSelector: React.FC = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'paymentDialog' });
  const { setSelectedUser, setCurrentStep, setCart } =
    usePaymentDialogContext();

  const [showGuestFields, setShowGuestFields] = useState(false);

  const fetchUserMutation = useMutation({
    mutationFn: (userId: string) => fetchUser({ id: userId }),
    onSuccess: (user) => {
      setSelectedUser(user);
      setCart((prevCart) => ({
        ...prevCart,
        userId: user.id,
      }));
      setCurrentStep(PaymentStep.SelectType);
    },
  });

  const handleClientSelect = (
    client: ClientSearchResult | ClientSearchResult[] | null
  ) => {
    if (client && !Array.isArray(client)) {
      fetchUserMutation.mutate(client.id.toString());
    }
  };

  const handleContinueAsGuest = () => {
    if (!showGuestFields) {
      setShowGuestFields(true);
      return;
    }
  };

  const handleGuestFormSubmit = (values: {
    guestEmail: string;
    guestFirstName: string;
    guestLastName: string;
  }) => {
    setSelectedUser(undefined);
    setCart((prevCart) => ({
      ...prevCart,
      userId: '',
      guestCheckout: true,
      guestEmail: values.guestEmail || '',
      guestFirstName: values.guestFirstName || '',
      guestLastName: values.guestLastName || '',
    }));
    setCurrentStep(PaymentStep.SelectType);
  };

  const handleBackToLogin = () => {
    setShowGuestFields(false);
  };

  return (
    <Stack spacing={3}>
      {!showGuestFields ? (
        <>
          <Stack spacing={2}>
            <ClientAutocomplete
              value={null}
              onChange={handleClientSelect}
              label={''}
              autoFocus
              placeholder={t('searchForClient')}
            />
          </Stack>

          <Divider />

          <Stack spacing={2}>
            <Button
              variant="bordered"
              onClick={handleContinueAsGuest}
              fullWidth
            >
              {t('continueAsGuest')}
            </Button>
          </Stack>
        </>
      ) : (
        <Formik
          initialValues={{
            guestEmail: '',
            guestFirstName: '',
            guestLastName: '',
          }}
          onSubmit={handleGuestFormSubmit}
        >
          {({ submitForm }) => (
            <Form>
              <Stack spacing={3}>
                <Stack spacing={2}>
                  <Typography variant="h6">{t('guestInformation')}</Typography>

                  <FormTextField
                    name="guestFirstName"
                    label={t('firstName')}
                    fullWidth
                    autoFocus
                  />

                  <FormTextField
                    name="guestLastName"
                    label={t('lastName')}
                    fullWidth
                  />

                  <FormTextField
                    name="guestEmail"
                    label={t('email')}
                    type="email"
                    fullWidth
                  />
                </Stack>

                <Divider />

                <Stack spacing={2} direction="row">
                  <Button
                    variant="bordered"
                    onClick={handleBackToLogin}
                    fullWidth
                  >
                    {t('back')}
                  </Button>
                  <Button variant="prominent" onClick={submitForm} fullWidth>
                    {t('continue')}
                  </Button>
                </Stack>
              </Stack>
            </Form>
          )}
        </Formik>
      )}
    </Stack>
  );
};
